import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './App.css';
import { EC_LABEL, EC_TOOLTIP } from './labels';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// CEO and Stock Market Factoids
const FACTOIDS = [
  "Alex Karp, CEO of Palantir, holds a PhD in philosophy from Stanford and believes philosophical thinking enhances decision-making in tech.",
  "Mark Zuckerberg started Facebook in his Harvard dorm room in 2004 and became the world's youngest billionaire at age 23.",
  "Anthony Noto, CEO of SoFi, was previously Twitter's CFO and helped take the company public in 2013.",
  "Jensen Huang, NVIDIA's CEO, co-founded the company in 1993 at a Denny's restaurant booth in San Jose.",
  "The New York Stock Exchange bell has been rung to open trading every day since 1903, except during market closures.",
  "Warren Buffett bought his first stock at age 11 and filed his first tax return at age 13, claiming his bicycle as a business expense.",
  "The term 'bull market' comes from the way bulls attack by thrusting their horns upward, while bears swipe downward with their paws.",
  "Elon Musk became the richest person in the world largely due to Tesla's stock price, which increased over 740% in 2020.",
  "The NASDAQ was the world's first electronic stock exchange when it launched in 1971.",
  "Reed Hastings, Netflix CEO, was inspired to start the company after being charged a $40 late fee for a Blockbuster rental.",
  "The S&P 500 index was created in 1957 and originally included 500 of the largest U.S. companies by market capitalization.",
  "Satya Nadella, Microsoft's CEO, transformed the company's focus to cloud computing, increasing its market cap by over $1 trillion.",
  "The stock market crash of 1929 wiped out $30 billion in market value, equivalent to about $396 billion today.",
  "Tim Cook, Apple's CEO, wakes up at 3:45 AM every day and is known for his operational excellence and supply chain expertise.",
  "The term 'blue chip stocks' comes from poker, where blue chips traditionally have the highest value.",
  "Brian Chesky, Airbnb's CEO, funded the early company by selling Obama O's and Cap'n McCain's cereal boxes for $40 each.",
  "The Dow Jones Industrial Average was created in 1896 and originally included just 12 companies, mostly railroads.",
  "Mary Barra became the first female CEO of a major automaker when she took over General Motors in 2014.",
  "The largest single-day point gain in Dow Jones history was 2,113 points on March 24, 2020, during the COVID-19 pandemic.",
  "Andy Jassy, Amazon's CEO, started Amazon Web Services (AWS) in 2006, which now generates over $70 billion in annual revenue.",
  "The concept of 'market capitalization' was first used in the early 1900s to measure a company's total value.",
  "Daniel Zhang, former Alibaba CEO, created the Singles' Day shopping event, now the world's largest online shopping day."
];

const CACHE_BUSTER = Date.now(); // Force browser to reload
console.log('🚀 STEP 7 PRODUCTION CUTOVER - CACHED EXPERIENCE ACTIVE - Cache buster:', CACHE_BUSTER);
console.log('🏛️ Revolutionary Greek symbols with 55-second timing and $0.0044 economics!');

// *** USER TIMEZONE LOCALIZATION ***
// Convert UTC timestamp to user's local timezone for better UX
const formatTimestampForUser = (utcTimestamp) => {
  if (!utcTimestamp) return 'Not available';
  
  try {
    // Parse UTC timestamp from backend
    const utcDate = new Date(utcTimestamp);
    
    // Get user's timezone
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const timezoneName = new Date().toLocaleDateString('en', {timeZoneName: 'short'}).split(', ')[1];
    
    // Convert to user's local time
    const localTime = utcDate.toLocaleString('en-US', {
      timeZone: userTimezone,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
    
    return `${localTime} ${timezoneName}`;
  } catch (error) {
    console.error('Error formatting timestamp:', error);
    return 'Time not available';
  }
};

const App = () => {
  const [screeningResults, setScreeningResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [currentFactoid, setCurrentFactoid] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [dataStatus, setDataStatus] = useState(null);
  const [currentStep, setCurrentStep] = useState('');
  const [currentJobId, setCurrentJobId] = useState(null);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [screeningCompleted, setScreeningCompleted] = useState(false);
  const [manualLoadAvailable, setManualLoadAvailable] = useState(false);
  // 🟢🟡🔴 BRI Integration - State for popup management
  const [briPopup, setBriPopup] = useState({ show: false, stock: null, position: { x: 0, y: 0 } });
  
  // 🏢 Company Description Modal - State for popup management
  const [companyPopup, setCompanyPopup] = useState({ show: false, stock: null });

  // 🟢 Serene System (GT3) LIVE badge — today's signal from the engine that /signal runs on.
  // Was pointed at /backtest2/signal (the retired QQQ/MTUM Flip Flop) until 2026-10-03.
  const [ffLiveSignal, setFfLiveSignal] = useState(null);
  useEffect(() => {
    fetch(`${API}/gt3/signal`)
      .then((r) => r.json())
      .then((d) => { if (d?.status === 'success' && d.signal) setFfLiveSignal(d.signal); })
      .catch(() => {});
  }, []);

  // 📊 SPDR Sector ETF Ticker Mapping
  const sectorETFMap = {
    'Communication Services': 'XLC',
    'Communications': 'XLC',
    'Consumer Discretionary': 'XLY',
    'Consumer Staples': 'XLP',
    'Energy': 'XLE',
    'Financials': 'XLF',
    'Materials': 'XLB',
    'Real Estate': 'XLRE',
    'Health Care': 'XLV',
    'Healthcare': 'XLV',
    'Industrials': 'XLI',
    'Technology': 'XLK',
    'Information Technology': 'XLK',
    'Utilities': 'XLU'
  };

  const getSectorWithETF = (sectorName) => {
    const etf = sectorETFMap[sectorName];
    return etf ? `${sectorName} (${etf})` : sectorName;
  };

  // 🟢🟡🔴 BRI Integration - Helper functions
  const getBriColor = (classification) => {
    switch (classification) {
      case 'Green': return '#10b981'; // Green
      case 'Yellow': return '#f59e0b'; // Orange/Yellow  
      case 'Red': return '#ef4444'; // Red
      default: return '#6b7280'; // Gray for unknown
    }
  };

  const getBriSymbol = (classification) => {
    return '●'; // Circle symbol for all classifications
  };

  const handleBriClick = (stock, event) => {
    const rect = event.target.getBoundingClientRect();
    setBriPopup({
      show: true,
      stock: stock,
      position: {
        x: rect.left + window.scrollX,
        y: rect.bottom + window.scrollY + 5
      }
    });
  };

  const closeBriPopup = () => {
    setBriPopup({ show: false, stock: null, position: { x: 0, y: 0 } });
  };

  // 🏢 Company Description Modal - Handler functions
  const handleCompanyClick = (stock, event) => {
    event.preventDefault();
    setCompanyPopup({
      show: true,
      stock: stock
    });
  };

  const closeCompanyPopup = () => {
    setCompanyPopup({ show: false, stock: null });
  };

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (briPopup.show && !event.target.closest('.bri-popup') && !event.target.closest('.bri-indicator')) {
        closeBriPopup();
      }
      // Close company popup when clicking outside
      if (companyPopup.show && !event.target.closest('.company-popup') && !event.target.closest('.company-name-clickable')) {
        closeCompanyPopup();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [briPopup.show, companyPopup.show]);

  // Check market data status on component mount
  useEffect(() => {
    const checkDataStatus = async () => {
      try {
        const response = await axios.get(`${API}/market/data-status`);
        setDataStatus(response.data);
      } catch (err) {
        console.error('Failed to get data status:', err);
      }
    };
    checkDataStatus();
  }, []);

  // Cycle through factoids during loading
  useEffect(() => {
    let factoidInterval;
    let progressInterval;
    
    if (loading) {
      // Start progress simulation for fast comprehensive screening processing
      setProgress(0);
      setTimeRemaining(55); // *** STEP 7: 55 seconds for revolutionary cached experience ***
      
      // Update progress and time remaining
      progressInterval = setInterval(() => {
        setProgress(prev => {
          const newProgress = prev + (100 / 540); // Complete in 9 minutes
          if (newProgress >= 100) {
            setTimeRemaining(0);
            return 100;
          }
          setTimeRemaining(prev => Math.max(0, prev - 1));
          return newProgress;
        });
      }, 1000);
      
      // Cycle through factoids every 9 seconds
      factoidInterval = setInterval(() => {
        setCurrentFactoid(prev => (prev + 1) % FACTOIDS.length);
      }, 9000);
    }
    
    return () => {
      if (factoidInterval) clearInterval(factoidInterval);
      if (progressInterval) clearInterval(progressInterval);
    };
  }, [loading]);

  const runScreening = async () => {
    console.log('🚀 Starting screening process...');
    setLoading(true);
    setError(null);
    setProgress(0);
    setCurrentFactoid(0);
    setScreeningCompleted(false);
    setScreeningResults(null);
    
    try {
      console.log('📡 PHASE B STEP 7: Making API call to cached screening:', `${API}/cached-screening/start`);
      
      // *** STEP 7: PRODUCTION CUTOVER - SWITCH TO CACHED ENDPOINTS ***
      // Revolutionary 55-second Greek symbols experience with $0.0044 economics!

      // Start the cached screening experience
      console.log('📨 Sending cached screening request with Jon\'s revolutionary system');
      const startResponse = await axios.post(`${API}/cached-screening/start`, {}, {
        timeout: 10000 // Quick timeout for starting the cached job
      });
      
      console.log('✅ STEP 7: Cached screening start response:', startResponse.data);
      console.log('💡 User scan time:', startResponse.data.user_scan_time);
      console.log('💰 Revolutionary economics active:', startResponse.data.economics);
      
      const { job_id } = startResponse.data;
      setCurrentJobId(job_id);
      console.log('🆔 Cached job ID stored for PDF generation:', job_id);
      
      // Poll for progress every 3 seconds - SAME PATTERN, NEW ENDPOINT
      const pollInterval = setInterval(async () => {
        try {
          console.log(`🔄 STEP 7: Polling cached job ${job_id}...`);
          const statusResponse = await axios.get(`${API}/cached-screening/status/${job_id}`, {
            timeout: 15000  // Increased timeout for polling
          });
          const status = statusResponse.data;
          
          console.log('📊 STEP 7: Cached polling status:', status.status, `${status.progress}%`);
          console.log('👤 Personalized scan time:', status.user_scan_time);
          
          // Update progress from cached backend data
          setProgress(status.progress || 0);
          setCurrentStep(status.current_step || '');
          
          // SAME COMPLETION LOGIC - PROVEN AND RELIABLE
          if (status.status === 'completed') {
            clearInterval(pollInterval);
            console.log('✅ STEP 7: Cached screening completed!');
            console.log('🏛️ Greek symbols experience successful:', status.cache_info);
            console.log('Raw cached results:', status);
            console.log('Result data structure:', status.result);
            console.log('Setting cached screeningResults state...');
            
            // *** ADD TIMESTAMP TO RESULTS FOR USER TIMEZONE DISPLAY ***
            console.log('🕐 Backend timestamp data:', {
              user_scan_timestamp: status.user_scan_timestamp,
              user_scan_time_iso: status.user_scan_time_iso,
              user_scan_time: status.user_scan_time
            });
            
            const resultsWithTimestamp = {
              ...status.result,
              user_scan_timestamp: status.user_scan_timestamp || status.user_scan_time_iso,
              backend_user_scan_time: status.user_scan_time  // Keep backend formatted time as fallback
            };
            
            console.log('🎯 Results with timestamp:', resultsWithTimestamp);
            setScreeningResults(resultsWithTimestamp);
            setProgress(100);
            setTimeRemaining(0);
            setLoading(false);
            setScreeningCompleted(true);
            console.log('🎉 STEP 7 SUCCESS: Cached results loaded:', status.result);
          } else if (status.status === 'error') {
            clearInterval(pollInterval);
            setError(status.error_message || 'Screening failed');
            setLoading(false);
          }
        } catch (error) {
          console.warn('⚠️ Polling error (continuing):', error.response?.status, error.response?.data?.detail);
          // Don't stop polling on 404s - job might still be running
          if (error.response?.status === 500) {
            clearInterval(pollInterval);
            setError('Server error occurred');
            setLoading(false);
          }
        }
      }, 3000);

      // MOBILE BACKGROUND HANDLING: Resume polling when page becomes visible again
      const handleVisibilityChange = () => {
        if (!document.hidden && loading) {
          console.log('📱 Page became visible - ensuring polling is active');
          // The interval should still be running, but log for monitoring
        }
      };
      
      document.addEventListener('visibilitychange', handleVisibilityChange);
      
      // Cleanup function
      return () => {
        clearInterval(pollInterval);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }; // Simple 3-second polling like the working version
      
    } catch (err) {
      let errorMessage = 'Failed to start screening';
      
      if (err.code === 'ECONNABORTED') {
        errorMessage = 'Connection timeout - please try again';
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      setLoading(false);
      console.error('Screening start error:', err);
    }
  };

  const downloadPDFReport = async () => {
    console.log('🔍 PDF Download clicked! Current job ID:', currentJobId);
    
    if (!currentJobId) {
      console.error('❌ No job ID available');
      setError('No job ID available for PDF generation');
      return;
    }

    if (downloadingPDF) {
      console.log('⏳ PDF download already in progress, ignoring click');
      return; // Prevent multiple simultaneous downloads
    }

    console.log('📥 Starting PDF download for job:', currentJobId);
    setDownloadingPDF(true);
    
    try {
      console.log('🌐 STEP 7: Making PDF request to latest cached endpoint:', `${API}/cached-screening/report-latest`);
      const response = await axios.get(`${API}/cached-screening/report-latest`, {
        responseType: 'blob',
        timeout: 60000  // Increased timeout to 60 seconds for complex reports
      });

      console.log('✅ PDF response received:', response.status, response.headers);
      console.log('📊 Response data size:', response.data.size, 'bytes');

      // Create blob URL for the PDF
      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(pdfBlob);
      
      // Generate filename with current date
      const currentDate = new Date().toISOString().split('T')[0];
      const filename = `Pro_Stock_Money_BOWL_${currentDate}.pdf`;
      
      console.log('📁 PDF generated successfully, opening in new tab and triggering download');
      
      // ENHANCED SOLUTION: Both display in browser AND download with better reliability
      // 1. Open in new tab for immediate viewing (with slight delay to ensure blob is ready)
      setTimeout(() => {
        window.open(url, '_blank', 'noopener,noreferrer');
      }, 100);
      
      // 2. Also trigger download (works in most browsers) - with slight delay
      setTimeout(() => {
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }, 200);
      
      // Clean up blob URL after a longer delay
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 10000); // Increased cleanup delay
      
      console.log('✅ PDF opened in new tab and download triggered');
      
    } catch (err) {
      console.error('❌ PDF download error:', err);
      let errorMessage = 'Failed to download PDF report';
      
      if (err.response?.status === 404) {
        errorMessage = 'Report not found - please run screening again (data may have expired)';
      } else if (err.response?.status === 400) {
        errorMessage = 'Report not ready - please wait for screening to complete';
      } else if (err.response?.status === 405) {
        errorMessage = 'PDF generation temporarily unavailable - please try running screening again';
      } else if (err.code === 'ECONNABORTED') {
        errorMessage = 'Download timeout - report generation taking longer than expected. Please try again.';
      }
      
      setError(errorMessage);
      console.error('📋 Error details:', err.response?.data);
    } finally {
      setDownloadingPDF(false);
      console.log('🏁 PDF download process completed');
    }
  };

  const manualLoadResults = async () => {
    if (!currentJobId) return;
    
    console.log('🔄 Manual load triggered for job:', currentJobId);
    try {
      const response = await axios.get(`${API}/screening-status/${currentJobId}`, {
        timeout: 10000
      });
      const status = response.data;
      
      if (status.status === 'completed') {
        console.log('✅ Manual load successful!');
        setScreeningResults(status.result);
        setProgress(100);
        setTimeRemaining(0);
        setLoading(false);
        setScreeningCompleted(true);
        setManualLoadAvailable(false);
        console.log('🎉 Results loaded manually:', status.result);
      } else {
        console.log('⏳ Job still in progress:', status.status, status.progress + '%');
        setProgress(status.progress || 0);
        // Don't override our countdown timer with backend estimates
        // setTimeRemaining(status.estimated_time_remaining || 0);
        setCurrentStep(status.current_step || '');
      }
    } catch (error) {
      console.error('❌ Manual load failed:', error);
      setError('Failed to load results manually');
    }
  };

  return (
    <div className="app">
      <div className="container">
        <header className="header">
          <div className="header-content">
            <div className="logo-container">
              <img 
                src="/logo.png" 
                alt="QMP Logo" 
                className="company-logo"
              />
            </div>
            <h1 className="title">Pro Stock Money BOWL</h1>
            <h2 className="subtitle">AI Quantitative Stock Screener</h2>
            <p className="description">
              Enhanced multi-stage AI screening system with volume-weighted momentum analysis to identify high-potential growth stocks where institutional money is flowing
            </p>
          </div>
        </header>

        {/* Market Data Status Banner */}
        {dataStatus && (
          <div className={`data-status-banner ${dataStatus.data_source === 'live' ? 'live-data' : 'demo-data'}`}>
            <div className="data-status-content">
              <div className="data-source-info">
                <span className="data-source-label">
                  {dataStatus.data_source === 'live' ? '🟢 LIVE DATA' : '🟡 DEMO DATA'}
                </span>
                <span className="market-time">
                  {dataStatus.current_time_et} • Market: {dataStatus.market_status.toUpperCase()}
                </span>
              </div>
              <div className="data-status-note">
                {dataStatus.data_source === 'live' 
                  ? 'Showing last closing prices from Alpha Vantage'
                  : 'API limit reached - Using demo data until tomorrow 6:00 AM ET'
                }
              </div>
            </div>
          </div>
        )}

        {/* Progress Bar and Factoids (only show when loading) */}
        {loading && (
          <div className="progress-section">
            {/* 4-Stage Progress Boxes - Mobile-First Labor Illusion Design */}
            <div className="stages-container">
              <div className="stages-header">
                <h3>AI Institutional-Grade Analysis in Progress</h3>
                <div className="overall-timer">
                  {Math.floor(timeRemaining / 60)}:{(timeRemaining % 60).toString().padStart(2, '0')} Remaining
                </div>
              </div>
              
              {(() => {
                // *** STEP 7: OPTIMIZED GREEK SYMBOLS TIMING ***
                // Calculate current stage and progress with Jon's revolutionary timing
                const totalElapsed = 55 - timeRemaining; // 55 seconds total for cached experience
                
                // Stage durations matching Step 4 backend timing:
                // Ν (Nu): 12s, Ψ (Psi): 15s, Λ (Lambda): 18s, Ω (Omega): 10s
                const stageDurations = [12, 15, 18, 10]; // Total: 55 seconds
                const stageCumulativeTimes = [0, 12, 27, 45, 55]; // Cumulative timing
                
                let currentStageIndex = 0;
                let stageElapsed = totalElapsed;
                let stageProgress = 0;
                
                // Determine which stage we're in
                for (let i = 0; i < stageDurations.length; i++) {
                  if (totalElapsed <= stageCumulativeTimes[i + 1]) {
                    currentStageIndex = i;
                    stageElapsed = totalElapsed - stageCumulativeTimes[i];
                    stageProgress = Math.min((stageElapsed / stageDurations[i]) * 100, 100);
                    break;
                  }
                }
                
                // If completed, show final stage
                if (totalElapsed >= 55) {
                  currentStageIndex = 3;
                  stageProgress = 100;
                }
                
                const stages = [
                  {
                    id: 1,
                    icon: "/icons/nu.png",
                    title: "AI SECTOR INTELLIGENCE",
                    description: "Analyzing 11 SPDR sector ETFs using proprietary performance and volume-weighted momentum algorithms to identify institutional money flow patterns."
                  },
                  {
                    id: 2, 
                    icon: "/icons/psi.png",
                    title: "MULTI-FACTOR SCREENING",
                    description: "Applying our proprietary blend of RS Rating momentum indicators, liquidity thresholds, and break out proximity analysis."
                  },
                  {
                    id: 3,
                    icon: "/icons/lambda.png", 
                    title: "QUANTITATIVE ANALYSIS",
                    description: "Computing deep financial metrics using real-time Alpha Vantage data: Revenue and profit margin growth, EPS growth, FCF, and ROIC efficiency ratios."
                  },
                  {
                    id: 4,
                    icon: "/icons/omega.png",
                    title: "PORTFOLIO OPTIMIZATION", 
                    description: "Tagging cross-sector momentum stock selections with BRI scoring and alignment verification for optimal portfolio 'favor' and optimization."
                  }
                ];

                return (
                  <div className="stages-grid">
                    {stages.map((stage, index) => {
                      const isCompleted = index < currentStageIndex;
                      const isActive = index === currentStageIndex;
                      const isPending = index > currentStageIndex;
                      
                      return (
                        <div 
                          key={stage.id}
                          className={`stage-box ${isCompleted ? 'completed' : ''} ${isActive ? 'active' : ''} ${isPending ? 'pending' : ''}`}
                        >
                          <div className="stage-header">
                            <div className="stage-icon">
                              {isCompleted ? (
                                <div className="checkmark">✓</div>
                              ) : (
                                <img 
                                  src={stage.icon} 
                                  alt={stage.title}
                                  className="greek-symbol"
                                />
                              )}
                            </div>
                            <div className="stage-title-section">
                              <h4 className="stage-title">{stage.title}</h4>
                              <div className="stage-description">
                                {stage.description}
                              </div>
                            </div>
                            <div className="stage-status">
                              {isActive && (
                                <div className="stage-timer">
                                  {(() => {
                                    const stageDuration = stageDurations[currentStageIndex];
                                    const timeLeft = stageDuration - stageElapsed;
                                    return `${Math.floor(timeLeft / 60)}:${(timeLeft % 60).toString().padStart(2, '0')}`;
                                  })()}
                                </div>
                              )}
                              {isCompleted && (
                                <div className="stage-complete">COMPLETE</div>
                              )}
                              {isPending && (
                                <div className="stage-pending">PENDING</div>
                              )}
                            </div>
                          </div>
                          
                          {isActive && (
                            <div className="stage-progress">
                              <div className="stage-progress-bar">
                                <div 
                                  className="stage-progress-fill" 
                                  style={{ width: `${stageProgress}%` }}
                                ></div>
                              </div>
                              <div className="stage-progress-text">
                                {Math.round(stageProgress)}% Complete
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
            
            <div className="factoid-section">
              <h4 className="factoid-header">💡 Did You Know:</h4>
              <p className="factoid-text">{FACTOIDS[currentFactoid]}</p>
            </div>
          </div>
        )}

        {/* Screening Controls */}
        <div className="controls-section">
          <button 
            className="screen-button"
            onClick={runScreening}
            disabled={loading}
          >
            {loading ? 'Analyzing Stocks...' : screeningCompleted ? 'Boom! All done!' : 'Run Stock Screening'}
          </button>
          
          {error && (
            <div className="error-message">
              <p>Error: {error}</p>
            </div>
          )}
        </div>

        {/* Results Section */}
        {console.log('Render check - screeningResults:', screeningResults, 'loading:', loading)}
        {screeningResults && !loading && (
          <div className="results-section">
            {console.log('Rendering results section with data:', screeningResults)}
            {console.log('screeningResults.top_sectors:', screeningResults.top_sectors)}
            {console.log('Type of top_sectors:', typeof screeningResults.top_sectors)}
            {console.log('Array.isArray(top_sectors):', Array.isArray(screeningResults.top_sectors))}
            
            {/* *** PROFESSIONAL ANALYSIS HEADER *** */}
            <div className="analysis-header-section">
              <div className="analysis-timestamp-card">
                <h3>Your Custom AI Analysis Is Complete</h3>
                <div className="timestamp-info">
                  <span className="timestamp-label">Analysis Run:</span>
                  <span className="timestamp-value">
                    {screeningResults.user_scan_timestamp 
                      ? formatTimestampForUser(screeningResults.user_scan_timestamp)
                      : screeningResults.analysis_timestamp 
                        ? formatTimestampForUser(screeningResults.analysis_timestamp)
                        : screeningResults.backend_user_scan_time 
                          ? screeningResults.backend_user_scan_time + ' (Backend Time)'
                          : 'Analysis completed'
                    }
                  </span>
                </div>
              </div>
            </div>
            <div className="ec-legend" data-testid="ec-legend">
              <span className="ec-badge">●</span>
              <span>Highlighted rows marked <strong>{EC_LABEL}</strong> are <em>entry candidates</em> — a stock that closed above its resistance line on strong volume as of the latest session. It's a candidate to study for a next-open entry, not a recommendation.</span>
            </div>
            
            {/* Top Sectors Summary */}
            {screeningResults.top_sectors && screeningResults.top_sectors.length > 0 && (
              <div className="section-card">
                <h3>Top Performing Sectors {screeningResults.top_sectors?.[0]?.market_regime === 'shock' ? '(Risk-Off Mode)' : '(Normal Mode)'}</h3>
                <div className="sectors-grid">
                  {screeningResults.top_sectors.map((sector, index) => (
                    <div key={index} className="sector-card">
                      <h4>{getSectorWithETF(sector.sector)}</h4>
                      <div className="sector-metrics">
                        <p className="return-value">Relative Strength: {sector.relative_return_20d > 0 ? '+' : ''}{sector.relative_return_20d || sector.return_90d}%</p>
                        <p className="composite-score">Composite Score: {sector.composite_score}</p>
                        {sector.market_regime && (
                          <p className="regime-indicator" style={{color: sector.market_regime === 'shock' ? '#ff6b6b' : '#51cf66', fontWeight: 'bold'}}>
                            Regime: {sector.market_regime === 'shock' ? 'Risk-Off' : 'Normal'}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Stocks by Sector */}
            {screeningResults.stocks_by_sector && screeningResults.stocks_by_sector.map((sectorData, sectorIndex) => (
              <div key={sectorIndex} className="section-card">
                <h3>No. {sectorIndex + 1} - {getSectorWithETF(sectorData.sector)} Stocks ({sectorData.stocks.length} selections)</h3>
                <p className="process-info">
                  All {sectorData.max_stocks} qualifying stocks from the {getSectorWithETF(sectorData.sector)} sector — no per-sector cap
                </p>
                
                <div className="stocks-table-container">
                  <table className="stocks-table">
                    <thead>
                      <tr>
                        <th>Rank</th>
                        <th>Symbol</th>
                        <th className="ec-th" title={EC_TOOLTIP}>{EC_LABEL}</th>
                        <th>Company</th>
                        <th>Score</th>
                        <th>RS Rating</th>
                        <th>EPS Growth</th>
                        <th>ROIC</th>
                        <th>Rule of 40</th>
                        <th>Price</th>
                        <th>Stop Loss</th>
                        <th>BRI</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sectorData.stocks.map((stock, index) => (
                        <tr key={stock.symbol} className={stock.entry_candidate ? 'ec-row' : ''} data-testid={`explore-row-${stock.symbol}`}>
                          <td className="rank">#{index + 1}</td>
                          <td className="symbol">
                            <a 
                              href={`https://finance.yahoo.com/quote/${stock.symbol}/chart/`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="ticker-link"
                            >
                              {stock.symbol}
                            </a>
                          </td>
                          <td className="ec-cell" data-testid={`explore-ec-${stock.symbol}`}>
                            {stock.entry_candidate
                              ? <span className="ec-badge" title={`${EC_TOOLTIP} — closed above resistance ${stock.resistance ? `$${stock.resistance}` : ''} on ${stock.breakout_rvol ? `${stock.breakout_rvol}x` : 'high'} volume`}>{EC_LABEL}</span>
                              : <span className="ec-none">—</span>}
                          </td>
                          <td className="company-cell">
                            <div 
                              className="company-name company-name-clickable"
                              onClick={(e) => handleCompanyClick(stock, e)}
                              title="Click for company description"
                            >
                              {stock.company_name}
                            </div>
                            <div className="business-description">{stock.business_description}</div>
                          </td>
                          <td className="score">{stock.composite_score.toFixed(1)}</td>
                          <td className="metric-value">{stock.rs_rating?.toFixed(1)}%</td>
                          <td className="metric-value">{stock.quarterly_eps_growth?.toFixed(1)}%</td>
                          <td className="metric-value">{stock.roic?.toFixed(1)}%</td>
                          <td className="rule-of-40">{stock.rule_of_40?.toFixed(1)}</td>
                          <td className="price">${stock.current_price?.toFixed(2)}</td>
                          <td className="price">${stock.stop_loss_price?.toFixed(2)}</td>
                          <td className="bri-cell">
                            {stock.bri_classification ? (
                              <span 
                                className="bri-indicator"
                                style={{ color: getBriColor(stock.bri_classification) }}
                                onClick={(e) => handleBriClick(stock, e)}
                                title={`BRI Score: ${stock.bri_score} (${stock.bri_classification})`}
                              >
                                {getBriSymbol(stock.bri_classification)}
                              </span>
                            ) : (
                              <span className="bri-none">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Watchlist for easy copy-paste */}
                <div className="watchlist-section">
                  <span className="watchlist-label">Watchlist:</span>
                  <span className="watchlist-tickers">
                    {sectorData.stocks.map(stock => stock.symbol).join(',')}
                  </span>
                </div>
              </div>
            ))}

            {/* Methodology */}
            <div className="methodology-card">
              <h3>Methodology</h3>
              <p>{screeningResults.methodology_note}</p>
            </div>

            {/* PDF Download Section */}
            <div className="pdf-download-section">
              <div className="download-card">
                <h3>📄 Download Complete Report</h3>
                <p>Get a comprehensive PDF analysis including detailed methodology, quality assessment, and investment implications.</p>
                {console.log('🔍 PDF Button State - currentJobId:', currentJobId, 'downloadingPDF:', downloadingPDF)}
                <button 
                  className="download-pdf-button"
                  onClick={() => {
                    console.log('🖱️ PDF button clicked!');
                    downloadPDFReport();
                  }}
                  disabled={downloadingPDF || !currentJobId}
                >
                  {downloadingPDF ? (
                    <>
                      <span className="download-spinner"></span>
                      Generating PDF...
                    </>
                  ) : (
                    <>
                      📥 Download PDF Report
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 🟢🟡🔴 BRI Popup */}
      {briPopup.show && briPopup.stock && (
        <div 
          className="bri-popup"
          style={{
            position: 'absolute',
            left: `${briPopup.position.x}px`,
            top: `${briPopup.position.y}px`,
            zIndex: 1000
          }}
        >
          <div className="bri-popup-content">
            <div className="bri-popup-header">
              <span 
                className="bri-popup-indicator"
                style={{ color: getBriColor(briPopup.stock.bri_classification) }}
              >
                {getBriSymbol(briPopup.stock.bri_classification)}
              </span>
              <span className="bri-popup-title">
                {briPopup.stock.symbol} - BRI Rating
              </span>
              <button className="bri-popup-close" onClick={closeBriPopup}>×</button>
            </div>
            <div className="bri-popup-details">
              <div className="bri-score">
                <strong>Score:</strong> {briPopup.stock.bri_score}/10
              </div>
              <div className="bri-classification">
                <strong>Classification:</strong> 
                <span 
                  className={`bri-badge bri-${briPopup.stock.bri_classification?.toLowerCase()}`}
                  style={{ color: getBriColor(briPopup.stock.bri_classification) }}
                >
                  {briPopup.stock.bri_classification}
                </span>
              </div>
              <div className="bri-rationale">
                <strong>Rationale:</strong>
                <p>{briPopup.stock.bri_rationale}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 🏢 Company Description Popup */}
      {companyPopup.show && companyPopup.stock && (
        <div className="company-popup">
          <div className="company-popup-content">
            <div className="company-popup-header">
              <span className="company-popup-symbol">{companyPopup.stock.symbol}</span>
              <span className="company-popup-title">{companyPopup.stock.company_name}</span>
              <button className="company-popup-close" onClick={closeCompanyPopup}>×</button>
            </div>
            <div className="company-popup-details">
              <div className="company-popup-industry">
                <span className="industry-label">{companyPopup.stock.business_description}</span>
              </div>
              <div className="company-popup-description">
                <p>{companyPopup.stock.company_description_full || "Company description not available."}</p>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* 🟢 Serene System LIVE badge — fixed bottom-right, links to /signal */}
      {ffLiveSignal && (
        <a href="/signal" className="ff-live-badge" data-testid="ff-live-badge" title={ffLiveSignal.explainer || 'Serene System — open the live signal'}>
          <span className="ff-live-dot" />
          <span className="ff-live-label">LIVE NOW</span>
          <span className="ff-live-action">{ffLiveSignal.action} {ffLiveSignal.next_target}</span>
        </a>
      )}
    </div>
  );
};

export default App;